import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { ChevronUp, ChevronDown } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function PreReservationPage() {
  const { getAccessToken, handleApiError } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'descending' });

  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }
      
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.status_code === 401 && data.message === 'Invalid or expired token') {
        handleApiError(new Error('Invalid or expired token'));
        return null;
      }

      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      throw error;
    }
  }, [getAccessToken, navigate, handleApiError]);

  const fetchReservations = useCallback(async () => {
    try {
      const response = await fetchWithTokenRefresh('/api/pre_reservations/');
      if (response && response.status === 'success') {
        setReservations(response.data);
        setError('');
      }
    } catch (error) {
      // 에러 처리만 하고 추가 리다이렉션은 하지 않음
      console.error('사전예약자 정보 불러오기 실패:', error);
      setError('사전예약자 정보를 불러오는데 실패했습니다.');
    }
  }, [fetchWithTokenRefresh]);

  const sortedReservations = useCallback(() => {
    const sortableReservations = [...reservations];
    if (sortConfig.key) {
      sortableReservations.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableReservations;
  }, [reservations, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  return (
    <Layout>
      <div className="container mx-auto px-6 py-8">
          <div className="container mx-auto px-6 py-8">
            <h3 className="text-3xl font-medium text-gray-700">사전예약자 목록</h3>
            {error && <div className="mt-4 rounded bg-red-100 p-3 text-red-700">{error}</div>}
            <div className="mt-8">
              <table className="min-w-full leading-normal">
                <thead>
                  <tr>
                    <th className="border-b-2 border-gray-200 bg-gray-100 px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                      예약 ID
                    </th>
                    <th className="border-b-2 border-gray-200 bg-gray-100 px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                      이름
                    </th>
                    <th className="border-b-2 border-gray-200 bg-gray-100 px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                      전화번호
                    </th>
                    <th 
                      className="cursor-pointer border-b-2 border-gray-200 bg-gray-100 px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-600"
                      onClick={() => requestSort('created_at')}
                    >
                      <div className="flex items-center justify-center">
                        예약 일시
                        {sortConfig.key === 'created_at' && (
                          sortConfig.direction === 'ascending' ? <ChevronUp className="ml-1 size-4" /> : <ChevronDown className="ml-1 size-4" />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedReservations().map((reservation) => (
                    <tr key={reservation.reservation_id}>
                      <td className="border-b border-gray-200 bg-white p-5 text-center text-sm">
                        {reservation.reservation_id}
                      </td>
                      <td className="border-b border-gray-200 bg-white p-5 text-center text-sm">
                        {reservation.user_name}
                      </td>
                      <td className="border-b border-gray-200 bg-white p-5 text-center text-sm">
                        {reservation.mobile_number}
                      </td>
                      <td className="border-b border-gray-200 bg-white p-5 text-center text-sm">
                        {new Date(reservation.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
    </Layout>
  );
}

export default PreReservationPage;
